/**
 * useTtsCoach — ElevenLabs TTS playback with IndexedDB cache (7d retention).
 *
 * Usage:
 *   const { speak, ready, voice, setVoice } = useTtsCoach();
 *   speak("Let us get started. Today is Push Day.");
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { apiClient } from "../services/api";

const DB_NAME = "fitfrik-tts";
const STORE = "audio";
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function getCached(key) {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => {
        const v = req.result;
        if (!v || Date.now() - v.t > TTL_MS) return resolve(null);
        resolve(v.url);
      };
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function setCached(key, url) {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put({ url, t: Date.now() }, key);
  } catch {
    /* ignore */
  }
}

export default function useTtsCoach() {
  const [available, setAvailable] = useState(false);
  const [voice, setVoiceState] = useState(() => localStorage.getItem("ff_tts_voice") || "calm_male");
  const [enabled, setEnabledState] = useState(() => localStorage.getItem("ff_tts_enabled") === "1");
  const audioRef = useRef(null);

  useEffect(() => {
    apiClient.get("/tts/status").then((r) => setAvailable(!!r.data?.available)).catch(() => setAvailable(false));
  }, []);

  const setVoice = (v) => {
    localStorage.setItem("ff_tts_voice", v);
    setVoiceState(v);
  };
  const setEnabled = (e) => {
    localStorage.setItem("ff_tts_enabled", e ? "1" : "0");
    setEnabledState(e);
  };

  const speak = useCallback(
    async (text) => {
      if (!enabled || !available || !text) return;
      const key = `${voice}::${text}`;
      let url = await getCached(key);
      if (!url) {
        try {
          const { data } = await apiClient.post("/tts/generate", { text, voice });
          url = data?.audio_url;
          if (url) await setCached(key, url);
        } catch {
          return;
        }
      }
      try {
        if (audioRef.current) {
          audioRef.current.pause();
        }
        const a = new Audio(url);
        a.volume = 0.9;
        audioRef.current = a;
        a.play().catch(() => {});
      } catch {
        /* ignore */
      }
    },
    [enabled, available, voice]
  );

  return { speak, available, voice, setVoice, enabled, setEnabled };
}
